/**
 * <subscribe-upgrade> — swaps a one-time cart line onto a selling plan.
 *
 * Follows the same contract as CartItems.updateQuantity in cart.js: collect the sections
 * that want re-rendering via the `cart:bundled-sections` event, POST to cart_change_url,
 * then publish `cartUpdate` so the drawer (and the cart page, if open) re-renders itself.
 * The line is swapped in place rather than added, so quantity and position are preserved.
 */
class SubscribeUpgrade extends HTMLElement {
  constructor() {
    super();

    this.button = this.querySelector('[data-upgrade]');
    this.button?.addEventListener('click', this.onClick.bind(this));
  }

  get line() {
    return parseInt(this.dataset.line, 10);
  }

  get quantity() {
    return parseInt(this.dataset.quantity, 10) || 1;
  }

  get sellingPlan() {
    return this.dataset.sellingPlan;
  }

  get variantId() {
    return this.dataset.variantId;
  }

  get currentVariantId() {
    return this.dataset.currentVariantId;
  }

  onClick() {
    if (this.loading) return;

    const line = this.line;
    if (!line || !this.sellingPlan) return;

    this.setLoading(true);

    const sectionsToBundle = [];
    document.documentElement.dispatchEvent(
      new CustomEvent('cart:bundled-sections', { bubbles: true, detail: { sections: sectionsToBundle } })
    );

    const onSuccess = (parsedState) => {
      theme.pubsub.publish(theme.pubsub.PUB_SUB_EVENTS.cartUpdate, {
        source: 'subscribe-upgrade',
        cart: parsedState,
        line: line
      });
      // the drawer replaces its own markup on cartUpdate, which removes this element,
      // so there is no loading state left to unwind on the success path
    };

    // The target plan is tied to a variant that may differ from the one already in this
    // cart line (each selling plan here lives on exactly one Count variant), and
    // /cart/change.js can only touch quantity/properties/selling_plan on the variant
    // already in the line — it can't swap the variant. When they differ, remove the line
    // and re-add it on the right variant instead of patching selling_plan in place.
    if (this.variantId && this.currentVariantId && this.variantId !== this.currentVariantId) {
      fetch(`${theme.routes.cart_change_url}`, {
        ...theme.utils.fetchConfig(),
        body: JSON.stringify({ line: line, quantity: 0, sections: sectionsToBundle })
      })
        .then((response) => response.json())
        .then(() => fetch(`${theme.routes.cart_add_url}`, {
          ...theme.utils.fetchConfig(),
          body: JSON.stringify({
            items: [{ id: parseInt(this.variantId, 10), quantity: this.quantity, selling_plan: this.sellingPlan }],
            sections: sectionsToBundle
          })
        }))
        .then((response) => response.json())
        .then((addedState) => {
          if (addedState.status || addedState.errors) {
            this.setError(addedState.description || addedState.message || addedState.errors);
            return;
          }
          // /cart/add.js only returns the added line(s) plus sections — no item_count, no
          // full items list. A no-op /cart/update.js call fetches the real cart shape (the
          // <cart-count> element reads cart.item_count off this event and renders "undefined"
          // otherwise) while still returning the freshly rendered sections.
          return fetch(`${theme.routes.cart_update_url}`, {
            ...theme.utils.fetchConfig(),
            body: JSON.stringify({ updates: {}, sections: sectionsToBundle })
          })
            .then((response) => response.json())
            .then(onSuccess);
        })
        .catch((error) => {
          console.error(error);
          this.setError();
        });
      return;
    }

    fetch(`${theme.routes.cart_change_url}`, {
      ...theme.utils.fetchConfig(),
      body: JSON.stringify({
        line: line,
        quantity: this.quantity,
        selling_plan: this.sellingPlan,
        sections: sectionsToBundle
      })
    })
      .then((response) => response.json())
      .then((parsedState) => {
        if (parsedState.status || parsedState.errors) {
          // description is what /cart/change.js returns for a rejected change
          this.setError(parsedState.description || parsedState.message || parsedState.errors);
          return;
        }

        onSuccess(parsedState);
      })
      .catch((error) => {
        console.error(error);
        this.setError();
      });
  }

  setLoading(state) {
    this.loading = state;
    this.toggleAttribute('loading', state);
    if (this.button) this.button.disabled = state;
  }

  setError(message) {
    this.setLoading(false);
    if (message) console.error(message);
  }
}

customElements.define('subscribe-upgrade', SubscribeUpgrade);
